import type { TableHeadCellProps as OriginalTableHeadCellProps } from 'src/components/table';

type TableHeadCellProps = OriginalTableHeadCellProps & {
  dataTestId?: string;
};
import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import { Chip, TableRow, TableCell, Typography } from '@mui/material';

import { FileThumbnail } from 'src/components/file-thumbnail';
import { useTable, TableNoData, getComparator, TableHeadCustom } from 'src/components/table';

import { formatDate } from './utils/policyUtils';

// ----------------------------------------------------------------------

// Added data-testid attributes to TABLE_HEAD for better UI testing
const TABLE_HEAD: TableHeadCellProps[] = [
  { id: 'ratingDate', label: 'Rating date', dataTestId: 'table-head-rating-date' },
  { id: 'expirationDate', label: 'Expiration date', dataTestId: 'table-head-expiration-date' },
  { id: 'status', label: 'Status', dataTestId: 'table-head-status' },
  { id: 'mod', label: 'Mod', width: 80, dataTestId: 'table-head-mod' },
  { id: 'arap', label: 'ARAP', width: 80, dataTestId: 'table-head-arap' },
  { id: '', width: 80, dataTestId: 'table-head-empty' },
];

type RatingHistoryProps = {
  width: string;
  viewPolicy: PolicyViewModel & { ratingHistory?: any[] };
};

type AttachmentButtonProps = {
  fileName: string;
};

// ----------------------------------------------------------------------

export function RatingHistory({ width, viewPolicy }: RatingHistoryProps) {
  const table = useTable({ defaultOrder: 'desc', defaultOrderBy: 'ratingDate' });

  let tableData = structuredClone(viewPolicy?.ratingHistory);
  const comparator = getComparator(table.order, table.orderBy);
  const stabilizedData = tableData?.map((el, index) => [el, index] as const);

  stabilizedData?.sort((a: any, b: any) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedData != undefined ? stabilizedData.map((el) => el[0]) : undefined;

  const renderContent = () => (
    <Card
      sx={[
        (theme) => ({
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: '2.5em',
          borderRadius: theme.shape.borderRadius,
          boxShadow: 'var(--customShadows-card)',
          width,
        }),
      ]}
      data-testid="rating-history-card-container"
    >
      <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="rating-history-card">
        <Box sx={{ padding: '1.5em' }} data-testid="rating-history-header">
          <Typography
            variant="h6"
            sx={{ color: 'text.primary' }}
            data-testid="rating-history-title"
          >
            Rating history
          </Typography>
        </Box>
        <Box
          data-testid="rating-history-table-container"
          sx={(theme) => ({
            maxHeight: '25em',
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0,
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '0.5em',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.action.hover,
              borderRadius: '0.25em',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: '0.25em',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.grey[600],
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.action.hover}`,
            scrollbarGutter: 'stable',
          })}
        >
          <Table size="small" sx={{ minWidth: '31.25em' }} data-testid="rating-history-table">
            <TableHeadCustom
              order={table.order}
              orderBy={table.orderBy}
              headCells={TABLE_HEAD}
              rowCount={viewPolicy.ratingHistory?.length}
              numSelected={table.selected.length}
              onSort={table.onSort}
              sx={{
                height: '3.5em',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.paper',
              }}
              data-testid="rating-history-table-head"
            />
            <TableBody data-testid="rating-history-table-body">
              {tableData?.map((row, index) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  data-testid={`rating-history-row-${index}`}
                >
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid={`rating-history-rating-date-${index}`}
                  >
                    {formatDate(row.ratingDate)}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid={`rating-history-expiration-date-${index}`}
                  >
                    {formatDate(row.expirationDate)}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'wrap' }}
                    data-testid={`rating-history-status-${index}`}
                  >
                    {row.status}
                    {row.isPreliminary && (
                      <Chip
                        sx={{ ml: 1 }}
                        label="Preliminary"
                        color="info"
                        variant="soft"
                        size="small"
                        data-testid={`rating-history-preliminary-chip-${index}`}
                      />
                    )}
                    {row.riskID && (
                      <Box
                        sx={{ color: 'text.secondary', font: 'caption' }}
                        data-testid={`rating-history-risk-id-${index}`}
                      >
                        ID: {row.riskID}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid={`rating-history-mod-${index}`}
                  >
                    {row.mod?.toFixed(2)}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid={`rating-history-arap-${index}`}
                  >
                    {row.arap?.toFixed(2)}
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap', textAlign: 'right', height: '4.75em' }}
                    data-testid={`rating-history-attachment-${index}`}
                  >
                    {row.attachment && <AttachmentButton fileName={row.attachment} />}
                  </TableCell>
                </TableRow>
              ))}
              <TableNoData notFound={!tableData?.length} data-testid="rating-history-no-data" />
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );

  return renderContent();
}

function AttachmentButton({ fileName }: AttachmentButtonProps) {
  return (
    <IconButton aria-label={`View attachment: ${fileName}`}>
      <FileThumbnail
        file={fileName}
        slotProps={{
          img: { alt: `Attachment: ${fileName}` },
          icon: { alt: `Attachment: ${fileName}` },
        }}
      />
    </IconButton>
  );
}
